import { ShieldX, Users as UsersIcon } from "lucide-react";
import { Navigate } from "@tanstack/react-router";

import { useCurrentUser } from "~/lib/auth";
import { type UserRecord, useUsers } from "~/lib/users";

import { InviteUserDialog } from "~/components/InviteUserDialog";
import { TableSkeleton } from "~/components/TableSkeleton";
import { UserRowActions } from "~/components/UserRowActions";

const STATUS_PILL: Record<UserRecord["status"], string> = {
    active: "status-pill--live",
    suspended: "status-pill--paused",
    locked: "status-pill--failed",
};

const ROLE_DESCR: Record<string, string> = {
    admin: "Full platform control",
    operator: "Ingestion + system health",
    analyst: "Query + investigate",
    customer: "Tenant data only",
};

export function UsersPage(): React.ReactElement {
    const me = useCurrentUser();
    const isAdmin = me.data?.roles.includes("admin");
    const { data: users, isLoading, isError, error } = useUsers();

    if (me.data && !isAdmin) {
        return <Navigate to="/" />;
    }

    const activeCount = users?.filter((u) => u.status === "active").length ?? 0;

    return (
        <div className="stack-lg">
            <header className="page-header">
                <div className="page-header__top">
                    <div className="page-header__heading">
                        <span className="page-header__eyebrow">
                            <span className="page-header__eyebrow__icon"><UsersIcon size={14} aria-hidden /></span>
                            Identity &amp; access
                        </span>
                        <h1 className="page-header__title">Users</h1>
                        <p className="page-header__description">
                            Internal staff (Admin / Operator / Analyst) and external customer users. Suspending
                            a user revokes their live sessions immediately. Resetting a password issues a
                            one-time temporary password — shown once, never again.
                        </p>
                    </div>
                    <div className="page-header__actions">
                        <InviteUserDialog />
                    </div>
                </div>
                <div className="page-header__meta">
                    <span className="page-header__meta__item tabular-nums">
                        {(users?.length ?? 0).toLocaleString()} total
                    </span>
                    {activeCount > 0 && (
                        <span className="page-header__meta__item tabular-nums" style={{ color: "var(--color-success)" }}>
                            {activeCount} active
                        </span>
                    )}
                </div>
            </header>

            <section className="section-card" aria-label="Users">
                {isLoading && <TableSkeleton rows={6} columns={5} />}
                {isError && (
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Could not load users</div>
                        <div className="empty-state__body">
                            {error instanceof Error ? error.message : "Unknown error"}
                        </div>
                    </div>
                )}
                {users && users.length === 0 && (
                    <div className="empty-state">
                        <UsersIcon size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No users yet</div>
                        <div className="empty-state__body">
                            Invite the first one above to get the platform staffed up.
                        </div>
                    </div>
                )}
                {users && users.length > 0 && (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Roles</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Status</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Created</th>
                                    <th style={{ width: 1 }} aria-label="Actions" />
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr key={u.id}>
                                        <td>
                                            <span className="text-13" style={{ wordBreak: "break-all" }}>
                                                {u.email}
                                            </span>
                                            {u.id === me.data?.id && (
                                                <span className="chip chip--accent" style={{ marginLeft: 8 }}>you</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="cluster-xs">
                                                {u.roles.map((r) => (
                                                    <span
                                                        key={r}
                                                        className="chip"
                                                        title={ROLE_DESCR[r] ?? r}
                                                    >
                                                        {r}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <span className={`status-pill ${STATUS_PILL[u.status]}`}>
                                                {u.status}
                                            </span>
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <span className="text-12 text-text-muted">
                                                {new Date(u.createdAt).toLocaleDateString(undefined, {
                                                    year: "numeric",
                                                    month: "short",
                                                    day: "numeric",
                                                })}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                            <UserRowActions user={u} isSelf={u.id === me.data?.id} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
